/**
 * @file        ProfileSlice.js
 * @module      store/User/ProfileSlice
 * @description Redux Toolkit slice for the logged-in user's personal profile.
 *              Owns the async thunk that persists edits to the user's profile
 *              via the backend and keeps the session's cached user object
 *              (store/Authentication/AuthSlice.js's `logedUser`) in sync with
 *              those edits.
 * @author      FreightSee Frontend Team
 * @date        2026-06-22
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { httpinstance } from "../../axios/api"
import { mergeLoggedInUser } from "../Authentication/AuthSlice"

/**
 * addprofiledetails
 * ------------------------------------------------------------------
 * Async thunk that saves the user's edited profile details.
 *
 * API endpoint : PUT /edit_profile
 *   Purpose     : Update the authenticated user's personal/profile details.
 *   Request     : `values` — form payload (fullname, emailid, countrycode,
 *                 phonenumber, address fields, city, pincode, state).
 *   Response    : {
 *                   statusCode: number,   // 200 on success
 *                   data: { ...partial user object with the updated fields }
 *                 }
 *
 * @param   {Object} values  Profile form values to persist.
 * @returns {Promise<Object>} The raw axios response (or err.response on failure)
 *                            so callers can read response.data.statusCode/data.
 */
export const addprofiledetails = createAsyncThunk('addprofiledetails', async (values, { dispatch })=>{
    try{
        const url = 'edit_profile'
        const response = await httpinstance.put(url,values)
        if(response?.data?.statusCode === 200){
            // WHY merge instead of replace:
            // The edit_profile response returns only the profile fields it updated,
            // NOT the complete user object. Overwriting the cached user outright would
            // drop fields the rest of the app depends on — most importantly:
            //   • userroles               -> drives ACL route resolution; losing it made
            //                                 the app redirect to "/undefined" on refresh.
            //   • kyc_verification_status -> gates the KYC tab's Edit button visibility;
            //                                 losing it hid the button after a profile save.
            // Merging into the session's `logedUser` (store/Authentication/AuthSlice.js)
            // preserves untouched fields while still applying the freshly-saved ones —
            // this used to merge into a `localStorage` "user" blob, but nothing
            // populates that anymore now that sessions are cookie-based (Task #99).
            dispatch(mergeLoggedInUser(response.data?.data));
        }
        return response
    }catch (err){
        // Return the error response (not throw) so the calling component can
        // inspect the status and surface a notification without a rejected thunk.
        return err.response
    }
})

/**
 * pincodes_by_state
 * ------------------------------------------------------------------
 * Async thunk that resolves a 6-digit pincode to its city area/state.
 *
 * API endpoint : POST /pincodes_by_state
 *   Request     : { zipcode: number }   // e.g. { zipcode: 201014 }
 *   Response    : { success: boolean, data: [ { zipcode, cityArea, state } ] }
 *
 * @param   {string} zipcode  The 6-digit pincode to look up.
 * @returns {Promise<Object>} The raw axios response (or err.response on failure).
 */
export const pincodes_by_state = createAsyncThunk('pincodes_by_state', async (zipcode)=>{
    try{
        const url = 'pincodes_by_state'
        // The backend stores zipcode as a Number, so a string won't match the
        // query — send it as a Number.
        const response = await httpinstance.post(url, { zipcode: Number(zipcode) })
        return response
    }catch (err){
        return err.response
    }
})

// This slice currently holds no client-side profile state of its own; the source
// of truth for the user is store/Authentication/AuthSlice.js's `logedUser`
// (updated above via mergeLoggedInUser) and the RolesSlice. Kept as a
// registered slice for future profile state needs.
const initialState = {}

const profileSlice = createSlice({
    name : 'profileslice',
    initialState,
    reducers:{}
})

export default profileSlice.reducer
